import { RecordValue, Schema } from '@coveo/bueno';
import { setContext, addContext, removeContext, } from '../../../features/context/context-actions.js';
import { contextReducer as context } from '../../../features/context/context-slice.js';
import { loadReducerError } from '../../../utils/errors.js';
import { validateInitialState } from '../../../utils/validate-payload.js';
import { buildController, } from '../../controller/headless-controller.js';
import { ReservedContextKeyError, isReservedContextKey, } from './headless-context-reserved-keys.js';
const initialStateSchema = new Schema({
    values: new RecordValue({
        options: { required: false },
    }),
});
/**
 * Creates a `Context` controller instance
 *
 * @param engine - The headless engine.
 * @param props - The configurable `Context` controller properties.
 *
 * @returns a `Context` controller instance.
 *
 * @group Controllers
 * @category Context
 */
export function buildCoreContext(engine, props = {}) {
    if (!loadContextReducers(engine)) {
        throw loadReducerError;
    }
    const controller = buildController(engine);
    const { dispatch } = engine;
    const getState = () => engine.state;
    const initialState = validateInitialState(engine, initialStateSchema, props.initialState, 'buildContext');
    if (initialState.values) {
        dispatch(setContext(initialState.values));
    }
    return {
        ...controller,
        get state() {
            return {
                values: getState().context.contextValues,
            };
        },
        set(context) {
            dispatch(setContext(context));
        },
        ...(getState().configuration.analytics.analyticsMode === 'legacy'
            ? legacyCoreContext(dispatch)
            : nextCoreContext(dispatch)),
    };
}
const legacyCoreContext = (dispatch) => ({
    add(contextKey, contextValue) {
        dispatch(addContext({ contextKey, contextValue }));
    },
    remove(key) {
        dispatch(removeContext(key));
    },
});
const nextCoreContext = (dispatch) => ({
    add(contextKey, contextValue) {
        if (isReservedContextKey(contextKey)) {
            throw new ReservedContextKeyError(contextKey);
        }
        dispatch(addContext({ contextKey, contextValue }));
    },
    remove(contextKey) {
        if (isReservedContextKey(contextKey)) {
            throw new ReservedContextKeyError(contextKey);
        }
        dispatch(removeContext(contextKey));
    },
});
function loadContextReducers(engine) {
    engine.addReducers({ context });
    return true;
}
